import { useState, useEffect, useRef } from 'react'

interface NavScrollState {
  scrolled: boolean // past threshold — triggers blur/border
  visible: boolean // false = hidden (scroll down), true = shown (scroll up)
}

/**
 * Tracks scroll direction for the navbar:
 * - scrolled: true once past `threshold` px (enables backdrop blur + border)
 * - visible:  false when scrolling DOWN past threshold, true when scrolling UP
 */
export const useNavScroll = (threshold = 80): NavScrollState => {
  const [scrolled, setScrolled] = useState(false)
  const [visible, setVisible] = useState(true)
  const lastY = useRef(0)

  useEffect(() => {
    const handler = () => {
      const y = window.scrollY

      setScrolled(y > 20)

      if (y < threshold) {
        // Near the top — always show
        setVisible(true)
      } else if (y > lastY.current + 4) {
        // Scrolling down (4px deadzone prevents micro-jitter)
        setVisible(false)
      } else if (y < lastY.current - 4) {
        // Scrolling up
        setVisible(true)
      }

      lastY.current = y
    }

    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [threshold])

  return { scrolled, visible }
}
